/**
 * BPF Vault Engraving Layer — Phase 5.3.0 (first customer-visible
 * engraving phase, desktop Vault only), viewport gating corrected
 * Phase 5.3.1.
 *
 * Owns exactly one thing: drawing the live engraving preview onto its
 * own canvas element, or hiding it. It does not know about form
 * fields, does not track builder state, and does not decide WHEN to
 * render — it only receives whatever assets/bpf-memorial-designer.js's
 * existing getCanvasState() already builds and hands to render(),
 * exactly the same contract BpfMemorialCanvasRenderer already has
 * (assets/bpf-memorial-canvas.js, which this file does not modify and
 * does not know exists — the two run side by side, never coupled).
 *
 * Why a separate canvas, not a change to BpfMemorialCanvasRenderer:
 * that file is proven, working, customer-facing code untouched since
 * Phase 3.2.1, and every engraving phase since 5.1.1 has deliberately
 * kept it that way. This canvas sits at the same z-index tier as
 * whichever plain <img> layer it's standing in for (see assets/bpf-
 * memorial-designer.css) and, when shown, contains a full redraw of
 * that same image plus the engraved text — an opaque replacement, not
 * a transparent overlay. This is required, not stylistic: the engraved
 * appearance (Phase 5.2.0/5.2.2) uses multiply/screen blend layers that
 * must composite against the real surface pixels to read as carved
 * rather than printed — a transparent overlay stacked on top of a
 * separate <img> element cannot blend with it; only pixels drawn into
 * the same canvas can. When hidden, the original plain <img> shows
 * through underneath exactly as it always has — an unsupported
 * product (most Capsules — see engravingSurface below), an empty name,
 * or any failure here all fall back to that untouched path.
 *
 * Dog Bone Capsule phase: engraving is no longer exclusively a Vault
 * concept. canvasState.visualAssets.engravingSurface ("base" or
 * "figure" for Vault, unchanged; "capsule" for an engraving-capable
 * Capsule — today only Dog Bone Capsule) is the single source of truth
 * for both WHETHER a resolved product supports engraving and WHICH
 * asset/color axis drives the redraw — never memorialStyle alone,
 * since most Capsules must never render engraving despite sharing the
 * same "Capsule" style string. See render()'s own comments for the
 * exact resolution.
 *
 * Viewport ownership: this file has no concept of desktop or mobile —
 * deliberately. It does not check window width, does not know about
 * breakpoints, and never decides on its own whether to run. Phase 5.3.0
 * assumed calling code would only invoke render() from desktop
 * triggers, but nothing actually enforced that; a customer typing a
 * Vault name on mobile still triggered a full render() call, base-image
 * load, and shadow-renderer construction. Phase 5.3.1 fixed this at
 * the caller, not here: assets/bpf-memorial-designer.js's
 * updateEngravingFromState() now gates every call behind a single
 * isDesktopViewport() check before render() is ever invoked — see that
 * method's own comment. On mobile, render() is simply never called;
 * this file has nothing to do differently and nothing here changed to
 * accommodate it, per "responsive trigger behavior belongs to the
 * Builder controller, not... the shadow renderer."
 *
 * Rendering is exposed to customers by that Builder-level scope alone,
 * not by the Engraving Profile's own lifecycle status — see
 * assets/bpf-engraving-geometry.js's header comment and docs/
 * engraving-profiles-README.md's Lifecycle section for why "calibrated"
 * is sufficient to render and "active" is a separate, later milestone.
 *
 * Failure handling: any error (missing data, a renderer exception, an
 * image load failure) hides this canvas and returns — it must never
 * throw into the Builder's own event handlers or block Add to Cart.
 * The plain base image is always the safe fallback.
 */

// Phase 5.3.3: generic string normalization only — turns a
// properties[Dog & Engraving Color] value ("Metallic Blue") into the
// Engraving Style Profile's color key ("metallic-blue"). Deliberately
// contains no color-specific branching: a sixth color needs only a
// new entry in engraving-style-profiles/standard.yml's `colors` map,
// never a change here. See that file for why this is now a separate
// axis from the Vault's own base plaque color.
function _normalizeEngravingColorKey(value) {
  return typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, '-') : '';
}

if (typeof window.BpfVaultEngravingLayer === 'undefined') {
  window.BpfVaultEngravingLayer = class BpfVaultEngravingLayer {
    constructor(canvasEl) {
      this.canvasEl = canvasEl || null;
      this._generation = 0;
      this._shadowRenderer = null; // lazy — only constructed the first time Vault actually needs to render
      this._data = null; // lazily parsed once from the embedded <script data-engraving-data> tag
      this._dataLoadAttempted = false;
    }

    _ensureShadowRenderer() {
      if (this._shadowRenderer) return this._shadowRenderer;
      if (typeof window.BpfEngravingGeometry === 'undefined' || typeof window.BpfEngravingShadowRenderer === 'undefined') {
        return null;
      }
      this._shadowRenderer = new window.BpfEngravingShadowRenderer({ geometry: window.BpfEngravingGeometry });
      return this._shadowRenderer;
    }

    _ensureData() {
      if (this._data || this._dataLoadAttempted) return this._data;
      this._dataLoadAttempted = true;
      const script = document.querySelector('[data-engraving-data]');
      if (!script) return null;
      try {
        this._data = JSON.parse(script.textContent);
      } catch (error) {
        this._data = null;
      }
      return this._data;
    }

    _hide() {
      if (this.canvasEl) this.canvasEl.hidden = true;
    }

    // Single public entry point. Reads the same canvasState object
    // assets/bpf-memorial-designer.js's getCanvasState() already
    // builds for BpfMemorialCanvasRenderer — no new state-gathering
    // logic, no duplicated lookups.
    //
    // Milestone 1 (Memorial Renderer): now returns a Promise<boolean>
    // (true if it actually drew and showed the canvas, false if it hid
    // it for any reason) instead of undefined — purely additive. Every
    // existing early-return path below is unchanged except for wrapping
    // its return value; the async image-load path resolves on the same
    // onload/onerror branches that already existed, at the same point
    // in time, doing the same work. Both existing callers (assets/bpf-
    // memorial-designer.js's updateEngravingFromState()/
    // renderEngravingForMobileReview(), assets/bpf-cart-engraving.js's
    // renderOne()) already call render(canvasState) and discard its
    // return value, so this is invisible to them. It exists so assets/
    // bpf-memorial-renderer.js can await completion before reading
    // pixels back off this same canvas via toBlob() — a one-shot export
    // has no "next render will eventually show the right thing" safety
    // net the way continuous on-screen updates do.
    render(canvasState) {
      if (!this.canvasEl) return Promise.resolve(false);
      this._generation += 1;
      const generation = this._generation;

      if (!canvasState) {
        this._hide();
        return Promise.resolve(false);
      }

      // Root-cause fix (Dog Bone Vault) + Dog Bone Capsule extension:
      // engravingSurface is the single capability/routing signal. Vault
      // records are engraving-capable exactly as before ("base" — most
      // Vaults engrave their own base plaque — or "figure" — Dog Bone
      // Vault's bone-shaped figure, confirmed by alpha-channel
      // inspection to be the only opaque surface at its calibrated
      // region). Capsule records are engraving-capable ONLY when
      // engravingSurface is explicitly "capsule" (so far, only Dog Bone
      // Capsule, whose calibration was likewise confirmed opaque
      // against its single flattened image) — every other Capsule
      // record inherits the harmless "base" default from the shared
      // visual-mapping reset block and is correctly rejected here,
      // exactly as every Capsule always has been. This is an exact
      // === "capsule" comparison, deliberately never a truthy/non-
      // default check — engravingSurface is a closed 3-value enum and
      // "base" is the shared reset default, so anything looser
      // (`!== "base"`, `Boolean(engravingSurface)`) would treat a
      // Vault-only value like "figure" leaking onto a Capsule record as
      // engraving-capable too. Mirrors assets/bpf-memorial-designer.js's
      // currentStyleSupportsEngraving() — same rule, same reasoning,
      // kept in sync deliberately (this file has no DOM access to call
      // that method directly, so the check is duplicated, not shared).
      const engravingSurface = (canvasState.visualAssets && canvasState.visualAssets.engravingSurface) || 'base';
      const supportsEngraving =
        canvasState.memorialStyle === 'Vault' || (canvasState.memorialStyle === 'Capsule' && engravingSurface === 'capsule');
      if (!supportsEngraving) {
        this._hide();
        return Promise.resolve(false);
      }

      const name = canvasState.engravedName || '';
      if (!name) {
        this._hide();
        return Promise.resolve(false);
      }

      // The color that both gates readiness and keys the asset map
      // differs by surface: "figure" is keyed by figureColor, "capsule"
      // by capsuleColor (Capsule has no Vault Color step at all — every
      // Capsule product only ever collects one color, and requiring
      // vaultColor here would incorrectly hide a fully-configured Dog
      // Bone Capsule), "base" (every other Vault) keyed by vaultColor,
      // unchanged from every prior phase.
      const surfaceColor =
        engravingSurface === 'figure'
          ? canvasState.figureColor
          : engravingSurface === 'capsule'
            ? canvasState.capsuleColor
            : canvasState.vaultColor;
      if (!surfaceColor) {
        this._hide();
        return Promise.resolve(false);
      }

      const surfaceAssetsByColor =
        canvasState.visualAssets &&
        (engravingSurface === 'figure'
          ? canvasState.visualAssets.figureAssetsByColor
          : engravingSurface === 'capsule'
            ? canvasState.visualAssets.flattenedAssetsByColor
            : canvasState.visualAssets.baseAssetsByColor);
      const baseEntry = surfaceAssetsByColor ? surfaceAssetsByColor[surfaceColor] : null;
      if (!baseEntry || !baseEntry.src) {
        this._hide();
        return Promise.resolve(false);
      }

      // Exposed as a plain DOM attribute (not a class) so any CSS file
      // hosting this canvas (Builder's assets/bpf-memorial-designer.css,
      // Cart's assets/bpf-cart-memorial-preview.css) can raise this
      // layer above the figure tier when it's redrawing the figure
      // itself, without this file needing to know either markup's BEM
      // class names. A no-op wherever no matching CSS selector exists
      // (e.g. the offscreen Renderer canvas, which has no stylesheet at
      // all and instead branches on this same engravingSurface value
      // directly — see assets/bpf-memorial-renderer.js).
      if (this.canvasEl.dataset) {
        this.canvasEl.dataset.engravingSurface = engravingSurface;
      }

      const data = this._ensureData();
      const renderer = this._ensureShadowRenderer();
      if (!data || !renderer) {
        this._hide();
        return Promise.resolve(false);
      }

      // Dog Bone Vault phase: data.profiles is a map keyed by the same
      // engravingProfileRef string each visual-mapping record carries
      // (see snippets/bpf-memorial-visual-mapping.liquid) — "standard"
      // for every ordinary breed (the Pitbull-calibrated geometry,
      // unchanged), "dog-bone-vault" for Dog Bone's own special base.
      // Falls back to "standard" for any canvasState that doesn't carry
      // visualAssets.engravingProfileRef at all (older/unrelated
      // callers, or a resolved record that simply never set one) —
      // preserves every existing caller's exact prior behavior. Missing
      // profile key entirely is the same "hide, don't guess" failure
      // path every other missing-data case in this file already takes.
      const profileKey = (canvasState.visualAssets && canvasState.visualAssets.engravingProfileRef) || 'standard';
      const profile = data.profiles && (data.profiles[profileKey] || data.profiles.standard);
      if (!profile || !profile.geometryProfile || !profile.styleProfile) {
        this._hide();
        return Promise.resolve(false);
      }

      // Engraving material color resolution order (narrowest override
      // wins): a future customer-facing "Engraving Color" field would
      // slot in ahead of both of these (not built yet — see
      // canvasState.engravingColorOverride, deliberately unused below,
      // documented here only so that future addition never has to
      // restructure this precedence chain); then
      // visualAssets.engravingColorDefault — a profile-level default
      // ('' for every ordinary breed/Capsule, meaning "no override, fall
      // through"; 'Black' for Dog Bone Vault, which never follows the
      // figure color — see snippets/bpf-memorial-visual-mapping.liquid);
      // then the surface-appropriate customer color selection —
      // figureColor for "base"/"figure" (the original and still-default
      // behavior for every ordinary Vault), capsuleColor for "capsule"
      // (Capsule has no figureColor at all — see the surfaceColor
      // resolution above).
      const engravingColorDefault = (canvasState.visualAssets && canvasState.visualAssets.engravingColorDefault) || '';
      const engravingColorFallback = engravingSurface === 'capsule' ? canvasState.capsuleColor : canvasState.figureColor;
      const engravingColorSource = canvasState.engravingColorOverride || engravingColorDefault || engravingColorFallback;
      const engravingColorKey = _normalizeEngravingColorKey(engravingColorSource);
      // Phase 5.3.4: normalized the same way as engravingColorKey — the
      // renderer uses this only to look up an optional contrast-
      // adjusted override recipe (see resolveColorRecipe in
      // bpf-engraving-shadow.js); it never changes which asset loads,
      // that's still baseEntry.src above. Keyed off surfaceColor (not
      // always vaultColor) so the contrast lookup reflects whatever
      // color is actually behind the text: the pedestal's own color for
      // every "base" surface (unchanged, still the raw capitalized
      // vaultColor), the figure's own color for "figure" surfaces (Dog
      // Bone Vault), or the Capsule's own color for "capsule" surfaces
      // (Dog Bone Capsule) — the pedestal color is irrelevant for either
      // of the latter two, since the pedestal isn't what the text sits on.
      const surfaceColorKey = _normalizeEngravingColorKey(surfaceColor);
      const loader = new Image();
      // Phase 5.3.2B: the base image is a Shopify CDN theme asset
      // (asset_url) drawn onto this canvas, and bpf-engraving-shadow.js's
      // render() calls targetCanvas.toDataURL() for its returned
      // diagnostic dataUrl field — reading pixel data back from a canvas
      // that has ever had a cross-origin image drawn onto it without an
      // explicit CORS-enabled load throws SecurityError ("Tainted
      // canvases may not be exported"), even though drawing itself
      // succeeds silently. crossOrigin must be set before src is
      // assigned — setting it after has no effect, since the browser
      // captures the CORS mode at request time. Shopify's shop-assets
      // CDN (which serves asset_url URLs) sends
      // Access-Control-Allow-Origin: * at the edge, confirmed via direct
      // HTTP inspection during the Phase 5.3.2B investigation, so this
      // is expected to succeed rather than turn the image load into a
      // hard failure.
      loader.crossOrigin = 'anonymous';
      return new Promise((resolve) => {
        loader.onload = () => {
          if (generation !== this._generation) {
            resolve(false);
            return;
          }
          try {
            const ctx = this.canvasEl.getContext('2d');
            ctx.clearRect(0, 0, this.canvasEl.width, this.canvasEl.height);
            ctx.drawImage(loader, 0, 0, this.canvasEl.width, this.canvasEl.height);
            renderer.render(profile.geometryProfile, profile.styleProfile, name, this.canvasEl.width, this.canvasEl.height, {
              baseColor: engravingColorKey,
              vaultColor: surfaceColorKey,
              targetCanvas: this.canvasEl,
            });
            this.canvasEl.hidden = false;
            resolve(true);
          } catch (error) {
            // Never break the page for a customer — the plain base <img>
            // layer beneath is already showing correctly either way.
            this._hide();
            resolve(false);
          }
        };
        loader.onerror = () => {
          if (generation !== this._generation) {
            resolve(false);
            return;
          }
          this._hide();
          resolve(false);
        };
        loader.src = baseEntry.src;
      });
    }

    // Phase 5.3.1: called on a desktop -> mobile viewport transition.
    // Bumps the generation counter (so any in-flight base-image load
    // started before the transition sees a stale generation on
    // completion and no-ops, per the same guard render() already uses
    // — see its onload/onerror handlers) and hides the canvas. Unlike
    // destroy() below, the instance remains fully usable afterward — a
    // later mobile -> desktop transition calls render() again normally.
    invalidate() {
      this._generation += 1;
      this._hide();
    }

    // Documented, always-safe cleanup hook, mirroring
    // BpfMemorialCanvasRenderer.destroy()'s own contract.
    destroy() {
      this._generation += 1;
    }
  };
}
